import { useEffect, useState } from "react"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { fetchWithAuth } from "@/lib/api/client"

interface MonthData {
  mes: string
  total: number
  ganadas: number
  perdidas: number
  activas: number
}

export function ChartLicitacionesMes() {
  const [data, setData] = useState<MonthData[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedYear, setSelectedYear] = useState<string>(
    new Date().getFullYear().toString()
  )

  // Generar array de años disponibles (últimos 5 años + año actual)
  const currentYear = new Date().getFullYear()
  const availableYears = Array.from({ length: 5 }, (_, i) =>
    (currentYear - i).toString()
  )

  useEffect(() => {
    async function fetchData() {
      setLoading(true)
      try {
        const result = await fetchWithAuth(
          `/api/v1/analytics/licitaciones/mes?year=${selectedYear}`
        )
        setData(result)
      } catch (error) {
        console.error("Error fetching licitaciones por mes:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [selectedYear])

  if (loading) {
    return <ChartLicitacionesMesLoading />
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-7">
        <CardTitle>Licitaciones por Mes</CardTitle>
        <Select value={selectedYear} onValueChange={setSelectedYear}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Año" />
          </SelectTrigger>
          <SelectContent>
            {availableYears.map((year) => (
              <SelectItem key={year} value={year}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="mes"
              className="text-xs"
              tick={{ fill: "hsl(var(--muted-foreground))" }}
            />
            <YAxis
              className="text-xs"
              tick={{ fill: "hsl(var(--muted-foreground))" }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--background))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "var(--radius)",
              }}
              labelStyle={{
                color: "hsl(var(--foreground))",
                fontWeight: 600,
                marginBottom: "8px",
              }}
              itemStyle={{
                color: "hsl(var(--foreground))",
              }}
            />
            <Legend
              wrapperStyle={{
                paddingTop: "20px",
              }}
            />
            <Bar
              dataKey="total"
              fill="hsl(var(--primary))"
              name="Total"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="ganadas"
              fill="hsl(142 76% 36%)"
              name="Ganadas"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="perdidas"
              fill="hsl(0 84% 60%)"
              name="Perdidas"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="activas"
              fill="hsl(45 93% 47%)"
              name="Activas"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

function ChartLicitacionesMesLoading() {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-10 w-[120px]" />
        </div>
      </CardHeader>
      <CardContent>
        <Skeleton className="h-[350px] w-full" />
      </CardContent>
    </Card>
  )
}
